/**
 * O texto de um PDF, lido no navegador.
 *
 * Papel: aqui ficam `extrairTextoDoPdf`, que abre o arquivo com o pdf.js e
 * devolve a camada de texto linha a linha; `extrairItensDoPdf`, que devolve os
 * pedaços de texto de cada página com a posição de cada um, para quem agrupa as
 * linhas do seu jeito; e as duas partes puras — `agruparEmLinhas`, que monta as
 * linhas de uma página a partir dos pedaços, e `textoDasPaginas`, que junta as
 * páginas.
 *
 * Como usar: o arquivo nunca sai da máquina — quem chama lê o texto e decide o
 * que mostrar. O pdf.js é carregado por `import()` no primeiro uso, e o
 * _worker_ vem empacotado pelo Vite (`?url`), e não de CDN; a primeira leitura
 * demora o que o pacote leva para baixar.
 *
 * Um PDF escaneado não tem camada de texto, e o resultado é só a linha em branco
 * de cada página: quem chama não acha nada e avisa que não identificou o
 * conteúdo.
 */

/**
 * O PDF não abriu por causa da senha.
 *
 * `motivo` é `necessaria` quando o arquivo está protegido e nenhuma senha foi
 * dada, e `incorreta` quando a senha dada não abre o arquivo. A mensagem é fixa
 * e nunca carrega a senha nem o texto do pdf.js, para poder ir direto à tela.
 */
export class ErroDeSenhaDoPdf extends Error {
  /** Por que o arquivo não abriu: falta a senha, ou a que foi dada não serve. */
  readonly motivo: 'necessaria' | 'incorreta';

  /**
   * @param motivo - `necessaria` ou `incorreta`, conforme o código do pdf.js.
   */
  constructor(motivo: 'necessaria' | 'incorreta') {
    super(
      motivo === 'necessaria'
        ? 'Este PDF está protegido por senha.'
        : 'A senha informada não abre este PDF.',
    );
    this.name = 'ErroDeSenhaDoPdf';
    this.motivo = motivo;
  }
}

/**
 * Traduz a falha do `getDocument` em `ErroDeSenhaDoPdf`, quando for dela.
 *
 * O pdf.js lança um `PasswordException` com `name` igual a `PasswordException` e
 * `code` 1 (`NEED_PASSWORD`) ou 2 (`INCORRECT_PASSWORD`). Qualquer outra falha,
 * ou um `PasswordException` com outro código, devolve `null` e segue como veio.
 *
 * @param falha - O que o `getDocument(...).promise` rejeitou.
 * @returns O erro de senha, ou `null` quando a falha não é de senha.
 */
function comoErroDeSenha(falha: unknown): ErroDeSenhaDoPdf | null {
  if (typeof falha !== 'object' || falha === null) return null;
  if (!('name' in falha) || falha.name !== 'PasswordException') return null;
  if (!('code' in falha)) return null;
  if (falha.code === 1) return new ErroDeSenhaDoPdf('necessaria');
  if (falha.code === 2) return new ErroDeSenhaDoPdf('incorreta');
  return null;
}

/** Um pedaço de texto da página, com a posição de onde foi desenhado. */
export interface ItemDeTexto {
  x: number;
  y: number;
  texto: string;
}

/** A distância de Y acima da qual um pedaço abre linha nova. */
const TOLERANCIA_DA_LINHA = 2;

/**
 * Monta as linhas de uma página.
 *
 * Ordena os pedaços de cima para baixo (`y` decrescente) e, no empate, da
 * esquerda para a direita; abre linha nova quando o `y` se afasta mais de 2 do
 * `y` do **primeiro** pedaço da linha, que não muda depois dele. Dentro da
 * linha, ordena por `x` e junta com um espaço. O pdf.js entrega os pedaços na
 * ordem em que foram desenhados, que numa tabela nem sempre é a de leitura.
 *
 * @param itens - Os pedaços com texto da página.
 * @returns As linhas, cada uma terminada em `\n`.
 */
export function agruparEmLinhas(itens: readonly ItemDeTexto[]): string {
  const ordenados = [...itens].sort((a, b) => b.y - a.y || a.x - b.x);
  const linhas: ItemDeTexto[][] = [];
  let yDaLinha: number | null = null;
  for (const item of ordenados) {
    const atual = linhas.at(-1);
    if (
      atual === undefined ||
      yDaLinha === null ||
      Math.abs(item.y - yDaLinha) > TOLERANCIA_DA_LINHA
    ) {
      linhas.push([item]);
      yDaLinha = item.y;
    } else {
      atual.push(item);
    }
  }
  return linhas
    .map(
      (linha) =>
        `${[...linha]
          .sort((a, b) => a.x - b.x)
          .map((item) => item.texto)
          .join(' ')}\n`,
    )
    .join('');
}

/**
 * Junta as páginas de um PDF num texto só.
 *
 * @param paginas - Os pedaços de texto de cada página, na ordem.
 * @returns As linhas de cada página, com uma linha em branco a mais no fim de
 *   cada uma.
 */
export function textoDasPaginas(paginas: readonly (readonly ItemDeTexto[])[]): string {
  return paginas.map((itens) => `${agruparEmLinhas(itens)}\n`).join('');
}

/**
 * Lê os pedaços de texto de cada página de um PDF.
 *
 * Quem chama é a importação do extrato do Secullum, que agrupa as linhas com a
 * tolerância própria, e `extrairTextoDoPdf`. Os pedaços só de espaço ficam de
 * fora; o texto dos outros vem como o pdf.js o entrega, sem aparar.
 *
 * @param arquivo - O PDF escolhido.
 * @param aoLerPagina - Chamado antes de ler cada página, com o número dela (a
 *   partir de 1) e o total, para a tela dizer onde a leitura está.
 * @param senha - Senha do PDF, quando ele tiver uma; sem ela, um PDF protegido
 *   rejeita com `ErroDeSenhaDoPdf` de motivo `necessaria`. A senha vai só ao
 *   pdf.js e nunca é guardada nem repetida em mensagem.
 * @returns Os pedaços de cada página, na ordem das páginas.
 * @throws O erro do pdf.js quando o arquivo não é um PDF que ele lê.
 */
export async function extrairItensDoPdf(
  arquivo: Blob,
  aoLerPagina?: (pagina: number, total: number) => void,
  senha?: string,
): Promise<ItemDeTexto[][]> {
  const [pdfjs, worker] = await Promise.all([
    import('pdfjs-dist'),
    import('pdfjs-dist/build/pdf.worker.min.mjs?url'),
  ]);
  if (pdfjs.GlobalWorkerOptions.workerSrc === '') {
    pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
  }

  const documento = await pdfjs
    .getDocument({ data: new Uint8Array(await arquivo.arrayBuffer()), password: senha })
    .promise.catch((falha: unknown) => {
      throw comoErroDeSenha(falha) ?? falha;
    });
  const paginas: ItemDeTexto[][] = [];
  for (let numero = 1; numero <= documento.numPages; numero += 1) {
    aoLerPagina?.(numero, documento.numPages);
    const pagina = await documento.getPage(numero);
    const conteudo = await pagina.getTextContent();
    paginas.push(
      conteudo.items.flatMap((item) =>
        'str' in item && item.str.trim() !== ''
          ? [{ x: item.transform[4] ?? 0, y: item.transform[5] ?? 0, texto: item.str }]
          : [],
      ),
    );
  }
  return paginas;
}

/**
 * Lê a camada de texto de um PDF.
 *
 * Quem chama são as importações da folha, da guia DAE e do salário, com o
 * arquivo que a pessoa escolheu. A falha de leitura do pdf.js chega a quem chama
 * como erro, e a mensagem dele fala do arquivo, nunca do conteúdo.
 *
 * @param arquivo - O PDF escolhido.
 * @param senha - Senha do PDF, quando ele tiver uma; ver `extrairItensDoPdf`.
 * @returns O texto, página a página, por `textoDasPaginas`.
 * @throws O erro do pdf.js quando o arquivo não é um PDF que ele lê.
 */
export async function extrairTextoDoPdf(arquivo: Blob, senha?: string): Promise<string> {
  return textoDasPaginas(await extrairItensDoPdf(arquivo, undefined, senha));
}
